'use client';

import type React from 'react';
import type { FormEvent } from 'react';
import { Button } from 'src/components/Button';
import { InputFile } from 'src/components/InputFile';
import { InputText } from 'src/components/InputText';
import {
  FormInputGrade,
  FormInputGradeSize,
} from 'src/features/createProduct/types/types';
import type { UseFormReturn } from 'src/hooks';
import type { Grade } from 'src/model/Grade';
import type { GradeSize } from 'src/model/GradeSize';
import type { JSX } from 'react/jsx-runtime';
import { Edit2, Trash2, Package, AlertCircle } from 'lucide-react';

interface GradeFormUI
  extends Pick<UseFormReturn, 'formData' | 'formErrors' | 'onChangeFormInput'> {
  isEdit: boolean;
  variants: Grade[];
  filesImageGrade: File[];
  previewGradesSizes: GradeSize[];
  addVariant: (e: FormEvent) => void;
  removeVariant: (index: string) => void;
  onChangeFormFileInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
  addSize: (e: FormEvent) => void;
  onEditGradeSize?: (
    size: GradeSize,
    gradeSizeId: string,
    gradeId: string
  ) => void;
  previewFilesGrade: string;
}

export const GradeForm: React.FC<GradeFormUI> = ({
  formData,
  formErrors,
  variants,
  filesImageGrade,
  previewFilesGrade,
  previewGradesSizes,
  isEdit,
  onEditGradeSize,
  onChangeFormInput,
  addVariant,
  removeVariant,
  onChangeFormFileInput,
  addSize,
}): JSX.Element => {
  return (
    <div className="space-y-6">
      <InputText
        label=""
        name="hiddenField"
        type="hidden"
        id="hiddenField"
        value={formData.hiddenField}
        onChange={(e) =>
          onChangeFormInput(formData.hiddenField)(e.target.value)
        }
        error={formErrors.hiddenField && formErrors.hiddenField[0]}
      />

      {/* Informações de Ajuda */}
      <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-red-900 mb-2">
              Como criar variações
            </h4>
            <ul className="text-sm text-red-800 space-y-1">
              <li className="flex items-start gap-2">
                <span className="text-red-600 mt-0.5">•</span>
                <span>
                  Cada variação precisa de pelo menos um tamanho (pode ser
                  genérico como Único)
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-600 mt-0.5">•</span>
                <span>
                  Adicione todos os tamanhos necessários antes de criar a
                  variação
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-600 mt-0.5">•</span>
                <span>Formatos de imagem aceitos: JPEG, JPG ou PNG</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Formulários de Criação */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Formulário de Variação */}
        <div className="bg-white border-2 border-red-200 rounded-xl p-6 space-y-4">
          <div className="flex items-center gap-3 pb-3 border-b border-red-100">
            <div className="w-8 h-8 rounded-lg bg-red-600 text-white flex items-center justify-center font-bold text-sm">
              1
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">
                Informações da Variação
              </h3>
              <p className="text-sm text-gray-600">Nome e imagem</p>
            </div>
          </div>

          <div className="space-y-4">
            <InputText
              label="Nome da Variação"
              name="color"
              type="text"
              id="color"
              value={formData[FormInputGrade.color as keyof typeof formData]}
              onChange={(e) =>
                onChangeFormInput(FormInputGrade.color)(e.target.value)
              }
              error={formErrors.color?.[0]}
              inputClassName="rounded-lg px-4 py-2.5 w-full bg-white border-2 border-gray-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 transition-all"
              placeholder="Ex: Azul Marinho, Vermelho, etc."
            />

            <div className="space-y-2">
              <InputFile
                name="mediaFilesGrade"
                label="Imagem da Variação"
                multiple
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) onChangeFormFileInput(e);
                }}
                error={
                  filesImageGrade.length > 5
                    ? 'Máximo de 5 arquivos permitidos'
                    : undefined
                }
              />

              {previewFilesGrade && (
                <div className="mt-3">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Preview:
                  </p>
                  <img
                    src={previewFilesGrade || '/placeholder.svg'}
                    alt="Preview da variação"
                    className="w-full h-40 object-cover rounded-lg border-2 border-gray-300"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Formulário de Tamanho */}
        <div className="bg-white border-2 border-red-200 rounded-xl p-6 space-y-4">
          <div className="flex items-center gap-3 pb-3 border-b border-red-100">
            <div className="w-8 h-8 rounded-lg bg-red-600 text-white flex items-center justify-center font-bold text-sm">
              2
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">
                Adicionar Tamanhos
              </h3>
              <p className="text-sm text-gray-600">Preço e estoque</p>
            </div>
          </div>

          <div className="space-y-4">
            <InputText
              label="Tamanho"
              name="size"
              type="text"
              id="size"
              value={formData[FormInputGradeSize.size]}
              onChange={(e) =>
                onChangeFormInput(FormInputGradeSize.size)(e.target.value)
              }
              error={formErrors.size?.[0]}
              inputClassName="rounded-lg px-4 py-2.5 w-full bg-white border-2 border-gray-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 transition-all"
              placeholder="Ex: P, M, G, 38, 40, Único"
            />

            <div className="grid grid-cols-2 gap-3">
              <InputText
                label="Preço"
                name="price"
                type="number"
                id="price"
                value={formData[FormInputGradeSize.price]}
                onChange={(e) =>
                  onChangeFormInput(FormInputGradeSize.price)(e.target.value)
                }
                error={formErrors.price?.[0]}
                inputClassName="rounded-lg px-4 py-2.5 w-full bg-white border-2 border-gray-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 transition-all"
                placeholder="R$ 0,00"
              />

              <InputText
                label="Desconto (%)"
                name="discount"
                type="number"
                id="discount"
                value={formData[FormInputGradeSize.discount]}
                onChange={(e) =>
                  onChangeFormInput(FormInputGradeSize.discount)(e.target.value)
                }
                error={formErrors.discount?.[0]}
                inputClassName="rounded-lg px-4 py-2.5 w-full bg-white border-2 border-gray-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 transition-all"
                placeholder="0"
              />
            </div>

            <InputText
              label="Estoque"
              name="stock"
              type="number"
              id="stock"
              value={formData[FormInputGradeSize.stock]}
              onChange={(e) =>
                onChangeFormInput(FormInputGradeSize.stock)(e.target.value)
              }
              error={formErrors.stock?.[0]}
              inputClassName="rounded-lg px-4 py-2.5 w-full bg-white border-2 border-gray-300 focus:border-red-500 focus:ring-2 focus:ring-red-200 transition-all"
              placeholder="Quantidade disponível"
            />

            <Button
              type="button"
              title="Adicionar Tamanho"
              buttonStyle="primary"
              size="sm"
              onClick={addSize}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-2.5 rounded-lg transition-colors"
            />
          </div>

          {/* Lista de Tamanhos Adicionados */}
          <div className="pt-4 border-t border-gray-200">
            <p className="text-sm font-semibold text-gray-700 mb-2">
              Tamanhos adicionados:
            </p>
            {previewGradesSizes && previewGradesSizes.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {previewGradesSizes.map((sizes, index) =>
                  sizes ? (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 border border-red-300 text-red-800"
                    >
                      {sizes.size}
                    </span>
                  ) : null
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">
                Nenhum tamanho adicionado ainda
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Botão de Criar Variação */}
      <div className="flex justify-center pt-4">
        <Button
          type="button"
          title="Criar Variação Completa"
          buttonStyle="text"
          size="sm"
          onClick={addVariant}
          className="bg-red-600 hover:bg-red-700 text-white font-semibold px-8 py-3 rounded-lg shadow-md hover:shadow-lg transition-all"
        />
      </div>

      {/* Lista de Variações Cadastradas */}
      {variants && variants.length > 0 && (
        <div className="mt-8">
          <div className="flex items-center gap-2 mb-4">
            <h3 className="text-xl font-bold text-gray-900">
              Variações Cadastradas
            </h3>
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-red-600 text-white text-sm font-bold">
              {variants.length}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {variants.map((variant, variantIndex) => (
              <div
                key={variant.id || variantIndex}
                className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow"
              >
                {/* Cabeçalho da Variação */}
                <div className="bg-gradient-to-r from-red-600 to-red-700 px-4 py-3">
                  <h4 className="font-bold text-white text-lg">
                    {variant.color}
                  </h4>
                </div>

                <div className="p-4 space-y-4">
                  {/* Imagem da Variação */}
                  {variant.image && (
                    <img
                      src={
                        variant.id
                          ? `${process.env.REACT_APP_BASE_URL}/uploads/${variant.image}`
                          : variant.image
                      }
                      alt={variant.color}
                      className="w-full h-48 object-cover rounded-lg border border-gray-200"
                    />
                  )}

                  {/* Lista de Tamanhos */}
                  <div className="space-y-3">
                    {variant.gradeSizes && variant.gradeSizes.length > 0 ? (
                      variant.gradeSizes.map((size, sizeIndex) => (
                        <div
                          key={size.id || sizeIndex}
                          className="bg-gray-50 rounded-lg p-3 space-y-2 border border-gray-200"
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-gray-900">
                              Tamanho: {size.size}
                            </span>
                            <span className="text-sm bg-red-100 text-red-800 px-2 py-1 rounded-full font-medium">
                              Estoque: {size.stock}
                            </span>
                          </div>

                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <span className="text-gray-600">Preço:</span>
                              <p className="font-semibold text-gray-900">
                                R$ {Number(size.price.price).toFixed(2)}
                              </p>
                            </div>
                            <div>
                              <span className="text-gray-600">Desconto:</span>
                              <p className="font-semibold text-red-600">
                                {size.price.discount}%
                              </p>
                            </div>
                          </div>

                          {isEdit && onEditGradeSize && (
                            <Button
                              type="button"
                              title="Editar Tamanho"
                              buttonStyle="outline"
                              size="sm"
                              onClick={() =>
                                onEditGradeSize(
                                  size,
                                  size.id || '',
                                  variant.id || ''
                                )
                              }
                              className="w-full mt-2 text-red-600 border-red-300 hover:bg-red-50 font-medium py-1.5 rounded-lg transition-colors flex items-center justify-center gap-2"
                            >
                              <Edit2 className="w-4 h-4" />
                              Editar
                            </Button>
                          )}
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500 italic">
                        Nenhum tamanho cadastrado
                      </p>
                    )}
                  </div>
                </div>

                {/* Rodapé com botão de excluir */}
                <div className="px-4 pb-4">
                  <Button
                    type="button"
                    title="Excluir Variação"
                    buttonStyle="outline"
                    size="sm"
                    onClick={() =>
                      removeVariant(variant.id || String(variantIndex))
                    }
                    className="w-full text-red-600 border-red-300 hover:bg-red-50 font-medium py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Excluir Variação
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Estado Vazio */}
      {variants && variants.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 font-medium">
            Nenhuma variação cadastrada ainda
          </p>
          <p className="text-sm text-gray-500 mt-1">
            Preencha os formulários acima e clique em Criar Variação Completa
          </p>
        </div>
      )}
    </div>
  );
};
